import React, { createContext, useContext, useState, useEffect } from 'react';
import ivaService from '../services/ivaService';
import { useCompany } from './CompanyContext';

/**
 * ------------------------------------------------------------------
 * IVA CONTEXT
 * ------------------------------------------------------------------
 * Maneja el estado global de Retenciones IVA
 * Proporciona funciones CRUD con caching para optimizar API calls
 * ------------------------------------------------------------------
 */

const IVAContext = createContext();

export function useIVAContext() {
    const context = useContext(IVAContext);
    if (!context) {
        throw new Error('useIVAContext debe usarse dentro de un IVAProvider');
    }
    return context;
}

export function IVAProvider({ children }) {
    const [retenciones, setRetenciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { company } = useCompany();

    /**
     * Cargar todas las retenciones
     * @param {boolean} force - Forzar recarga desde API
     */
    const fetchRetenciones = async (force = false) => {
        if (!company || !company.rif) {
            console.warn('No hay empresa seleccionada para cargar retenciones IVA');
            setRetenciones([]);
            return;
        }

        // Cache hit
        if (!force && retenciones.length > 0) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // Asumiendo que ivaService.getAll usa internamente company.rif si se necesita,
            // o si el endpoint devuelve todo filtraríamos, pero lo ideal es que el servicio o backend filtren.
            // Revisando useIVA original, llamaba a ivaService.getAll() sin params, pero puede que backend use session.
            const data = await ivaService.getAll({ empresa_rif: company.rif });
            const normalizedData = Array.isArray(data) ? data : (data.data || []);
            setRetenciones(normalizedData);
        } catch (err) {
            console.error('Error fetching retenciones IVA:', err);
            setError(err.message || 'Error al cargar retenciones IVA');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Crear una nueva retención (Header + Items)
     */
    const createRetencion = async (retencionData) => {
        if (!company || !company.rif) {
            throw new Error('No hay empresa seleccionada');
        }

        setLoading(true);
        try {
            // Agregar empresa_rif automáticamente
            const dataWithEmpresa = {
                ...retencionData,
                empresa_rif: company.rif
            };

            const result = await ivaService.createRetencion(dataWithEmpresa);
            // Al crear, lo más seguro es recargar para tener consistencia de IDs y totales
            await fetchRetenciones(true);
            return result;
        } catch (err) {
            console.error('Error creating retencion:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Limpiar caché cuando cambia la empresa
    useEffect(() => {
        setRetenciones([]);
    }, [company?.rif]);

    const value = {
        retenciones,
        loading,
        error,
        fetchRetenciones,
        createRetencion
    };

    return (
        <IVAContext.Provider value={value}>
            {children}
        </IVAContext.Provider>
    );
}
